/**
 * Subscription manager for the price stream WebSocket.
 *
 * Owns the per-page desired subscription set (game ids + league ids + sport
 * ids) and is responsible for telling the streaming-api / price-feed fanout
 * which games this connection wants, over whichever socket is active:
 *
 *   - Protobuf socket (legacy streaming-api): Subscribe / Unsubscribe /
 *     SubscribeAll ClientCommands, with `replace: true` semantics.
 *   - JSON socket (rendered price feed): `["priceFeedSubscribe", {scope…}]` /
 *     `["priceFeedUnsubscribe", {scope…}]` text messages. The fanout's API is
 *     additive (one scope per message), so the manager diffs desired vs.
 *     server state and emits only the changes. A fresh connection starts
 *     with the fanout's default all-games subscription; the fanout drops
 *     that default on the first scoped subscribe.
 *
 * Behavior:
 *   - All page-level intent goes through `setSingleGame`, `setLeagues`,
 *     `setSport`, `setFeatured`, `setScoped`, `setSubscribeAll`, or `clear`.
 *     Each fully replaces the desired set.
 *   - Diffs are emitted with a small debounce so rapid route changes coalesce.
 *   - On (re)connect, `attach` / `attachJSON` resets the server-side view and
 *     replays the current desired set on the fresh connection.
 *   - Disabled (no-op) when the env flag is off, so production can ship dark.
 */

import type { ProtobufWebSocketManager } from './stable-socket/ProtobufWebSocketManager';
import type { WebSocketManager } from './stable-socket/StableWebsocket';
import { encodeSubscribe, encodeSubscribeAll } from '../proto/encoder';

type Mode =
  | 'cleared'
  | 'singleGame'
  | 'league'
  | 'sport'
  | 'featured'
  | 'scoped'
  | 'all';

type Transport =
  | { kind: 'protobuf'; socket: ProtobufWebSocketManager }
  | { kind: 'json'; socket: WebSocketManager };

const FLUSH_DEBOUNCE_MS = 50;

const subscriptionsEnabled =
  String(import.meta.env.VITE_USE_PRICE_STREAM_SUBSCRIPTIONS).toLowerCase() ===
  'true';

class SubscriptionManager {
  private transport: Transport | null = null;

  private desiredGames = new Set<string>();
  private desiredLeagues = new Set<string>();
  private desiredSports = new Set<string>();
  private desiredMode: Mode = 'cleared';

  private serverGames = new Set<string>();
  private serverLeagues = new Set<string>();
  private serverSports = new Set<string>();
  // Whether the connection currently holds an all-games subscription. On the
  // JSON feed every fresh connection starts with the fanout's default one.
  private serverModeAll = false;

  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Whether the feature is enabled for this build. When false, all setters
   * are no-ops and the global broadcast-all behavior is preserved.
   */
  public isEnabled(): boolean {
    return subscriptionsEnabled;
  }

  /** True when the desired subscription set would receive updates for this game (always true when subscriptions are disabled) — telemetry uses this to tell real drains from navigate-away misses. */
  public covers(gameID?: string, leagueID?: string, sportID?: string): boolean {
    if (!subscriptionsEnabled) return true;
    if (this.desiredMode === 'all') return true;
    if (gameID && this.desiredGames.has(gameID)) return true;
    if (leagueID && this.desiredLeagues.has(leagueID)) return true;
    if (sportID && this.desiredSports.has(sportID)) return true;
    return false;
  }

  /**
   * Bind the manager to the active protobuf socket. Call again on reconnect
   * to re-replay the desired set.
   */
  public attach(socket: ProtobufWebSocketManager): void {
    if (!subscriptionsEnabled) return;
    this.transport = { kind: 'protobuf', socket };
    // Force a full replace on the new connection.
    this.serverGames = new Set();
    this.serverLeagues = new Set();
    this.serverSports = new Set();
    this.serverModeAll = false;
    this.scheduleFlush();
  }

  /**
   * Bind the manager to the active JSON (rendered price feed) socket. Call
   * on every open, including reconnects: a new connection is registered by
   * the fanout with its default all-games subscription and knows nothing of
   * what this page wanted before, so the desired set is replayed.
   */
  public attachJSON(socket: WebSocketManager): void {
    if (!subscriptionsEnabled) return;
    this.transport = { kind: 'json', socket };
    this.serverGames = new Set();
    this.serverLeagues = new Set();
    this.serverSports = new Set();
    this.serverModeAll = true;
    this.scheduleFlush();
  }

  /**
   * Drop the bound socket reference (e.g. when the manager is explicitly
   * closed).
   */
  public detach(): void {
    this.transport = null;
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  public setSingleGame(gameID: string): void {
    if (!subscriptionsEnabled) return;
    this.desiredGames = gameID ? new Set([gameID]) : new Set();
    this.desiredLeagues = new Set();
    this.desiredSports = new Set();
    this.desiredMode = 'singleGame';
    this.scheduleFlush();
  }

  /**
   * Subscribe to a set of leagues. Used by routes whose URL slug resolves to
   * one or more published league IDs (e.g. a `fighting` slug expanding to
   * `['UFCMMA', 'BOXING']`).
   */
  public setLeagues(leagueIDs: string[]): void {
    if (!subscriptionsEnabled) return;
    this.desiredGames = new Set();
    this.desiredLeagues = new Set(leagueIDs.filter(Boolean));
    this.desiredSports = new Set();
    this.desiredMode = 'league';
    this.scheduleFlush();
  }

  /**
   * Subscribe to every update for a sport. Used by `/exchange/:sport/all`
   * style routes where enumerating every league/game would be wasteful.
   */
  public setSport(sportID: string): void {
    if (!subscriptionsEnabled) return;
    this.desiredGames = new Set();
    this.desiredLeagues = new Set();
    this.desiredSports = sportID ? new Set([sportID]) : new Set();
    this.desiredMode = 'sport';
    this.scheduleFlush();
  }

  public setFeatured(gameIDs: string[]): void {
    if (!subscriptionsEnabled) return;
    this.desiredGames = new Set(gameIDs.filter(Boolean));
    this.desiredLeagues = new Set();
    this.desiredSports = new Set();
    this.desiredMode = 'featured';
    this.scheduleFlush();
  }

  /**
   * Subscribe to a combined scope of explicit games + leagues + sports.
   * Useful for pages that need broad route scope plus extra featured games.
   */
  public setScoped(
    gameIDs: string[],
    leagueIDs: string[],
    sportIDs: string[],
  ): void {
    if (!subscriptionsEnabled) return;
    this.desiredGames = new Set(gameIDs.filter(Boolean));
    this.desiredLeagues = new Set(leagueIDs.filter(Boolean));
    this.desiredSports = new Set(sportIDs.filter(Boolean));
    this.desiredMode = 'scoped';
    this.scheduleFlush();
  }

  public setSubscribeAll(): void {
    if (!subscriptionsEnabled) return;
    this.desiredGames = new Set();
    this.desiredLeagues = new Set();
    this.desiredSports = new Set();
    this.desiredMode = 'all';
    this.scheduleFlush();
  }

  public clear(): void {
    if (!subscriptionsEnabled) return;
    this.desiredGames = new Set();
    this.desiredLeagues = new Set();
    this.desiredSports = new Set();
    this.desiredMode = 'cleared';
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, FLUSH_DEBOUNCE_MS);
  }

  private flush(): void {
    const transport = this.transport;
    if (!transport || !transport.socket.isOpen()) return;
    if (transport.kind === 'protobuf') {
      this.flushProtobuf(transport.socket);
    } else {
      this.flushJSON(transport.socket);
    }
  }

  private flushProtobuf(socket: ProtobufWebSocketManager): void {
    if (this.desiredMode === 'all') {
      if (!this.serverModeAll) {
        socket.sendBinary(encodeSubscribeAll());
        this.serverModeAll = true;
        this.serverGames = new Set();
        this.serverLeagues = new Set();
        this.serverSports = new Set();
      }
      return;
    }

    // Anything other than `all` uses an explicit Subscribe(replace=true) so
    // server state is the authoritative truth on each transition. This is
    // simpler than diffing and keeps wire size tiny.
    const games = Array.from(this.desiredGames);
    const leagues = Array.from(this.desiredLeagues);
    const sports = Array.from(this.desiredSports);

    const sameGames = setsEqual(this.desiredGames, this.serverGames);
    const sameLeagues = setsEqual(this.desiredLeagues, this.serverLeagues);
    const sameSports = setsEqual(this.desiredSports, this.serverSports);
    if (sameGames && sameLeagues && sameSports && !this.serverModeAll) {
      return;
    }

    socket.sendBinary(
      encodeSubscribe(games, leagues, sports, /* replace */ true),
    );
    this.serverGames = new Set(games);
    this.serverLeagues = new Set(leagues);
    this.serverSports = new Set(sports);
    this.serverModeAll = false;
  }

  /**
   * JSON feed: the fanout API is additive, so emit the difference between
   * what the server has and what the page wants. Order matters in one place:
   * scoped subscribes go out BEFORE anything else, because the fanout drops
   * its default all-games subscription on the first scoped subscribe -- so a
   * page never passes through a "subscribed to nothing" moment while
   * narrowing from the default.
   */
  private flushJSON(socket: WebSocketManager): void {
    const send = (type: string, payload: Record<string, string>): boolean => {
      try {
        socket.send(JSON.stringify([type, payload]));
        return true;
      } catch {
        // Socket closed between the open check and the send; the next
        // attachJSON (on reconnect) replays the desired set.
        return false;
      }
    };

    if (this.desiredMode === 'all') {
      if (!this.serverModeAll) {
        if (!send('priceFeedSubscribe', { scope: 'all' })) return;
        this.serverModeAll = true;
      }
      // An all-games subscription covers everything; drop narrower scopes
      // left over from the previous page so the fanout state stays minimal
      // and a later narrowing starts from a clean slate.
      for (const gameID of this.serverGames) {
        send('priceFeedUnsubscribe', { scope: 'game', gameID });
      }
      for (const league of this.serverLeagues) {
        send('priceFeedUnsubscribe', { scope: 'league', league });
      }
      for (const sport of this.serverSports) {
        send('priceFeedUnsubscribe', { scope: 'sport', sport });
      }
      this.serverGames = new Set();
      this.serverLeagues = new Set();
      this.serverSports = new Set();
      return;
    }

    // Scoped (possibly empty) desired set: subscribes first, then
    // unsubscribes, then settle the all-games state.
    for (const gameID of this.desiredGames) {
      if (!this.serverGames.has(gameID)) {
        send('priceFeedSubscribe', { scope: 'game', gameID });
      }
    }
    for (const league of this.desiredLeagues) {
      if (!this.serverLeagues.has(league)) {
        send('priceFeedSubscribe', { scope: 'league', league });
      }
    }
    for (const sport of this.desiredSports) {
      if (!this.serverSports.has(sport)) {
        send('priceFeedSubscribe', { scope: 'sport', sport });
      }
    }
    for (const gameID of this.serverGames) {
      if (!this.desiredGames.has(gameID)) {
        send('priceFeedUnsubscribe', { scope: 'game', gameID });
      }
    }
    for (const league of this.serverLeagues) {
      if (!this.desiredLeagues.has(league)) {
        send('priceFeedUnsubscribe', { scope: 'league', league });
      }
    }
    for (const sport of this.serverSports) {
      if (!this.desiredSports.has(sport)) {
        send('priceFeedUnsubscribe', { scope: 'sport', sport });
      }
    }

    const anyScoped =
      this.desiredGames.size + this.desiredLeagues.size + this.desiredSports.size >
      0;
    if (this.serverModeAll) {
      if (anyScoped) {
        // The fanout removed its default all-games subscription when the
        // first scoped subscribe above arrived; nothing more to send.
        this.serverModeAll = false;
      } else {
        // The page wants nothing (account pages, etc.): drop the all-games
        // subscription explicitly so the connection goes quiet, matching
        // the protobuf "Subscribe(replace, empty)" semantics.
        if (send('priceFeedUnsubscribe', { scope: 'all' })) {
          this.serverModeAll = false;
        }
      }
    }

    this.serverGames = new Set(this.desiredGames);
    this.serverLeagues = new Set(this.desiredLeagues);
    this.serverSports = new Set(this.desiredSports);
  }
}

function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) {
    if (!b.has(v)) return false;
  }
  return true;
}

// Single shared instance — there is exactly one price stream socket per app.
export const subscriptionManager = new SubscriptionManager();
